import { Check } from "lucide-react";
import { memo, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import AppStatusPill from "@/components/layout/AppStatusPill";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useAppStatusTransitions } from "@/hooks/queries";
import type { AppStatusChoice } from "@/types/borrower";
import { cn } from "@/lib/utils";
import { formatDate } from "@/utils/dateUtils";

import { agentClient } from "../agentClient";
import { AGENT_CONTEXTS } from "../apps";
import { appLink, contextPath } from "../contexts/registry";
import { parseContextRef } from "../store/targets";
import type { WireChoice } from "../protocol/protocol";
import type { AgentState } from "../store/agentSlice";
import { selectChoice } from "../store/selectors";
import {
  AssistantCard,
  AssistantCardBody,
  AssistantCardFooter,
  AssistantCardHeader,
} from "../theme";
import { THREAD_CARD } from "./cardWidth";
import { FigureGrid } from "./FigureGrid";
import {
  choiceFields,
  choiceOutcome,
  choiceSubject,
  pickRows,
  type ChoiceOutcome,
  type PickRow,
} from "./wire";

/** The header badge once a pick is settled; a pick has no "declined". */
const RECEIPTS: Record<ChoiceOutcome, { label: string; className: string } | null> = {
  pending: null,
  approved: { label: "Picked", className: "bg-success-soft text-success" },
  declined: null,
  superseded: { label: "Superseded", className: "bg-muted text-muted-foreground" },
};

/** The button a row gets when its option names a context of this type. */
const VIEW_LABELS: Record<string, string> = { application: "View application" };

interface View {
  label: string;
  path: string;
}

/** A view button: labelled by what the ref names, going to `path`; null when either is missing. */
function viewOf(ref: string | null, path: string | null): View | null {
  const label = VIEW_LABELS[parseContextRef(ref)?.type ?? ""];
  return label && path ? { label, path } : null;
}

/**
 * Each row's view of its own option's ref; when no row has one, one for the card, from the
 * application the server says the whole pick is about. Never both.
 */
function pickViews(choice: WireChoice, rows: PickRow[]) {
  const rowViews = rows.map((row) =>
    viewOf(row.contextRef, contextPath(AGENT_CONTEXTS, row.contextRef))
  );
  const subject = rowViews.some(Boolean) ? null : choiceSubject(choice);
  const link = subject && appLink(AGENT_CONTEXTS, subject.url);
  // The link must open the application the server says the pick is about.
  const card = link && link.ref === subject.ref ? viewOf(link.ref, link.path) : null;
  return { rowViews, card };
}

/**
 * A pick (ask_user) as numbered rows. A tap decides it; a typed "2" or a name in the composer
 * does too (the server maps it), which is why the numbers show. Once settled, the chosen row
 * stays marked and the rest fade. The server's "None of these" is the last row, quiet and
 * unnumbered.
 */
export const PickCard = memo(function PickCard({ choiceRef }: { choiceRef: string }) {
  const choice = useSelector((state: { agent: AgentState }) => selectChoice(state, choiceRef));
  const rows = useMemo(() => (choice ? pickRows(choice) : []), [choice]);
  const figures = useMemo(() => (choice ? choiceFields(choice) : []), [choice]);
  const views = useMemo(() => (choice ? pickViews(choice, rows) : null), [choice, rows]);
  const { choices: statusChoices } = useAppStatusTransitions({
    enabled: rows.some((row) => row.subject?.appStatus),
  });
  const [pressed, setPressed] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  if (!choice || !views) return null;

  const outcome = choiceOutcome(choice);
  // "None of these" picked nothing: its row's mark says so, not a Picked badge.
  const pickedNone = rows.some((row) => row.system && row.id === choice.chosen);
  const receipt = pickedNone ? null : RECEIPTS[outcome];
  const chosen = choice.chosen ?? pressed;
  const pick = (optionId: string) => {
    setPressed(optionId);
    setError(null);
    agentClient.decide(choiceRef, optionId).catch((reason: { message?: string }) => {
      setPressed(null);
      setError(reason?.message || "That didn't go through. Try again.");
    });
  };

  return (
    <AssistantCard className={THREAD_CARD} data-outcome={outcome}>
      <AssistantCardHeader
        // The prompt wraps in full (it can name an application); the header truncates by default.
        // Top-aligned, so the badge stays on the first line when it wraps.
        className="items-start"
        title={<span className="whitespace-normal break-words">{choice.prompt}</span>}
      >
        {receipt && (
          <Badge className={cn("border-transparent", receipt.className)}>{receipt.label}</Badge>
        )}
      </AssistantCardHeader>
      {figures.length > 0 && (
        <AssistantCardBody>
          <FigureGrid figures={figures} />
        </AssistantCardBody>
      )}
      {/* Phones: flat rows between dividers, not boxes inside the card (rule 9). */}
      <ol
        aria-label={choice.prompt}
        className="flex flex-col gap-1.5 px-3.5 pb-3 pt-1 max-sm:gap-0 max-sm:divide-y max-sm:border-t max-sm:p-0"
      >
        {rows.map((row, index) => (
          <li key={row.id}>
            <PickButton
              row={row}
              view={views.rowViews[index]}
              chosen={chosen === row.id}
              settled={outcome !== "pending" && chosen !== row.id}
              disabled={outcome !== "pending" || pressed !== null}
              statusChoices={statusChoices}
              onPick={pick}
            />
          </li>
        ))}
      </ol>
      {error && (
        <p role="alert" className="px-3.5 pb-3 text-xs text-destructive">
          {error}
        </p>
      )}
      {views.card && (
        <AssistantCardFooter className="max-sm:border-t max-sm:py-1">
          {/* -ml-2: the text lines up with the card's own padding. */}
          <ViewButton {...views.card} className="-ml-2" />
        </AssistantCardFooter>
      )}
      <span role="status" className="sr-only">
        {receipt?.label}
      </span>
    </AssistantCard>
  );
});

function PickButton({
  row,
  view,
  chosen,
  settled,
  disabled,
  statusChoices,
  onPick,
}: {
  row: PickRow;
  view: View | null;
  chosen: boolean;
  settled: boolean;
  disabled: boolean;
  statusChoices: AppStatusChoice[];
  onPick: (optionId: string) => void;
}) {
  const { subject } = row;
  const broker = subject && [subject.brokerName, subject.brokerEmail].filter(Boolean).join(" · ");
  return (
    // The row is the box; the pick and the view button are siblings in it, never nested.
    <div
      className={cn(
        "flex items-center rounded-lg border border-input bg-background transition-colors has-[>button:first-child:active]:bg-accent",
        "max-sm:rounded-none max-sm:border-0",
        row.system && "border-transparent bg-transparent",
        chosen && "border-primary bg-accent"
      )}
    >
      <button
        type="button"
        // aria-disabled, not disabled: the row tapped keeps focus while the pick goes through.
        aria-disabled={disabled}
        aria-pressed={chosen}
        onClick={() => !disabled && onPick(row.id)}
        className={cn(
          "flex min-w-0 flex-1 items-start gap-2.5 rounded-lg px-2.5 py-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring aria-disabled:cursor-default",
          "max-sm:rounded-none max-sm:px-3.5 max-sm:py-3 max-sm:focus-visible:ring-inset",
          // Only the pick fades: its View application still makes sense after the choice.
          settled && "opacity-45"
        )}
      >
        {row.number === null ? (
          // No number, but the label lines up with the other rows' names; chosen, it holds the
          // check the numbered rows show with their filled badge.
          <span className="flex h-7 w-7 shrink-0 items-center justify-center" aria-hidden>
            {chosen && <Check className="h-4 w-4 text-primary" />}
          </span>
        ) : (
          <span
            className={cn(
              "flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-muted text-[11px] font-bold text-muted-foreground",
              chosen && "bg-primary text-primary-foreground"
            )}
          >
            {row.number}
          </span>
        )}
        {/* One line that wraps, never a stack; py-1 centres a single line on the 28px badge. */}
        {subject ? (
          <span className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1 py-1 leading-5">
            <span className="min-w-0 break-words text-[13.5px] font-semibold">{subject.name}</span>
            {subject.appStatus && (
              <AppStatusPill status={subject.appStatus} choices={statusChoices} />
            )}
            {subject.appliedAt && (
              <span className="whitespace-nowrap text-xs text-muted-foreground">
                Applied {formatDate(subject.appliedAt)}
              </span>
            )}
            {broker && (
              <span className="min-w-0 text-xs text-muted-foreground [overflow-wrap:anywhere]">
                {broker}
              </span>
            )}
          </span>
        ) : (
          <span
            className={cn(
              "min-w-0 flex-1 break-words py-1 text-[13.5px] leading-5",
              row.system && !chosen && "text-muted-foreground"
            )}
          >
            <span className={cn(!row.system && "font-semibold")}>{row.label}</span>
            {row.description && (
              <span className="text-xs text-muted-foreground"> {row.description}</span>
            )}
          </span>
        )}
      </button>
      {view && <ViewButton {...view} className="mr-1.5 max-sm:mr-2" />}
    </div>
  );
}

/** In-app navigation to what a row or the pick names; text only. */
function ViewButton({ label, path, className }: View & { className?: string }) {
  const navigate = useNavigate();
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className={cn("shrink-0 px-2 text-xs text-primary max-sm:h-10", className)}
      onClick={() => navigate(path)}
    >
      {label}
    </Button>
  );
}
